<template>
    <!-- The default slot is the <b-dropdown-item> rendered inside the connection
         actions dropdown; clicking it opens the modal. The <b-modal> is portaled
         to <body> by BootstrapVue, so it is not clipped by the dropdown. -->
    <span style="display: contents">
        <span style="display: contents" @click="open"><slot/></span>

        <b-modal :id="modalId" title="Targeted re-fetch" size="lg" :no-close-on-backdrop="loading">
            <p class="text-muted">
                Re-emit a chosen date/time window for the selected entities to S3. A repair tool
                for pipeline gaps — it does not disturb normal capture (own batch, no watermark
                changes; safe to re-run). Times are interpreted as UTC.
            </p>

            <div class="row">
                <div class="col-6">
                    <date-picker
                        id="targeted-from"
                        label="From (UTC)"
                        name="targeted_from"
                        placeholder="From (UTC)"
                        :config="dateConfig"
                        :value="from"
                        @input="from = $event"
                    ></date-picker>
                </div>
                <div class="col-6">
                    <date-picker
                        id="targeted-to"
                        label="To (UTC)"
                        name="targeted_to"
                        placeholder="To (UTC)"
                        :config="dateConfig"
                        :value="to"
                        @input="to = $event"
                    ></date-picker>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <app-select
                        label="Entities"
                        placeholder="All entities"
                        name="targeted_entities"
                        id="targeted-entities"
                        :is-multiple="true"
                        :use-search="true"
                        :options="entityOptions"
                        :value="selected"
                        @input="selected = $event"
                    ></app-select>
                    <small class="text-muted">
                        Choose "All entities" or leave empty to re-fetch every entity.
                    </small>
                </div>
            </div>

            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide(modalId)">Cancel</b-button>
                <b-button variant="primary" :disabled="loading" @click="submit">
                    Run re-fetch
                </b-button>
            </template>
        </b-modal>
    </span>
</template>

<script>
import axios from "axios";

const ALL = '__all__';

export default {
    props: {
        connectionId: {
            type: [String, Number],
            required: true
        },
        // Curated entity keys, e.g. ['user','course',...]; rendered as options.
        entities: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            loading: false,
            from: this.defaultFrom(),
            to: this.defaultTo(),
            selected: [],
        };
    },

    computed: {
        modalId() {
            return 'targeted-refetch-modal-' + this.connectionId;
        },
        dateConfig() {
            return {
                enableTime: true,
                altFormat: 'm/j/Y H:i',
                dateFormat: 'Y-m-d H:i',
            };
        },
        entityOptions() {
            const opts = [{ id: ALL, title: 'All entities', value: ALL }];
            this.entities.forEach((key) => {
                opts.push({ id: key, title: key, value: key });
            });
            return opts;
        }
    },

    methods: {
        open() {
            this.$bvModal.show(this.modalId);
        },
        // Resolve the entity payload: "All entities" or an empty pick => [] (=all).
        resolveEntities() {
            const picked = Array.isArray(this.selected) ? this.selected : [];
            if (picked.length === 0 || picked.includes(ALL)) {
                return [];
            }
            return picked;
        },
        submit() {
            if (!this.from || !this.to) {
                this.$notifications.error('Both From and To are required.');
                return;
            }
            if (this.from >= this.to) {
                this.$notifications.error('From must be before To (the window must be greater than zero).');
                return;
            }

            this.loading = true;
            return axios.post(
                this.route('moodle.targeted-refetch', { connection: this.connectionId }),
                { from: this.from, to: this.to, entities: this.resolveEntities() }
            ).then((response) => {
                const status = response.data && response.data.status;
                const ok = status === 'queued' || (status && status.status === 'queued');
                if (ok) {
                    this.$notifications.success('Targeted re-fetch queued. It runs on the next cron pass.');
                    this.$bvModal.hide(this.modalId);
                } else {
                    const msg = (status && status.data) ? status.data : 'Targeted re-fetch was not queued.';
                    this.$notifications.error(msg);
                }
                this.loading = false;
            }).catch(() => {
                this.$notifications.error('Targeted re-fetch request failed.');
                this.loading = false;
            });
        },
        pad(n) {
            return (n < 10 ? '0' : '') + n;
        },
        fmtUtc(d) {
            return d.getUTCFullYear() + '-' + this.pad(d.getUTCMonth() + 1) + '-' + this.pad(d.getUTCDate())
                + ' ' + this.pad(d.getUTCHours()) + ':' + this.pad(d.getUTCMinutes());
        },
        defaultFrom() {
            return this.fmtUtc(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
        },
        defaultTo() {
            return this.fmtUtc(new Date());
        }
    }
}
</script>
